import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Camera, Heart, Users } from 'lucide-react';
import SEO from '../components/SEO';
import PageTitle from '../components/PageTitle';
import { publicAPI } from '../services/apiService';
import { locationsManager } from '../utils/locationsManager';

const DynamicCityPage = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams();
  const { t, i18n } = useTranslation();
  
  // Use prop slug if provided, otherwise use param slug
  const slug = propSlug || paramSlug;
  const currentLang = i18n.language;
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationData, setLocationData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load location data from backend API first, then fallback to localStorage
        let location = null;
        
        try {
          // Try to load from API
          const allLocations = await locationsManager.initializeDefaultLocations();
          // Search by slug or by name (case-insensitive)
          location = allLocations.find(loc => 
            loc.slug === slug || 
            loc.slug?.toLowerCase().includes(slug.toLowerCase()) ||
            loc.name?.toLowerCase() === slug.toLowerCase()
          );
          
          if (!location) {
            // Fallback to localStorage
            location = locationsManager.getLocationBySlug(slug);
          }
        } catch (apiError) {
          console.warn('Failed to load from API, using localStorage:', apiError);
          location = locationsManager.getLocationBySlug(slug);
        }
        
        if (!location) {
          setError('Location not found');
          return;
        }
        
        // Debug log to see what data we have
        console.log(`Loading location data for ${slug}:`, location);
        setLocationData(location);
        
        // Set empty services for now - can be enhanced later
        setServices([]);
        
      } catch (error) {
        console.error('Error loading city page data:', error);
        setError('Error loading page data');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadData();
    }
  }, [slug]);

  // Listen for location updates
  useEffect(() => {
    const handleLocationsUpdate = async () => {
      try {
        // Reload locations from API to get latest data
        const allLocations = await locationsManager.initializeDefaultLocations();
        const location = allLocations.find(loc => loc.slug === slug);
        
        if (location) {
          setLocationData(location);
        } else {
          // If location not found in API, try localStorage
          const localLocation = locationsManager.getLocationBySlug(slug);
          if (localLocation) {
            setLocationData(localLocation);
          }
        }
      } catch (error) {
        console.warn('Error updating location data:', error);
        // Fallback to localStorage
        const location = locationsManager.getLocationBySlug(slug);
        if (location) {
          setLocationData(location);
        }
      }
    };
    
    window.addEventListener('locations-updated', handleLocationsUpdate);
    return () => window.removeEventListener('locations-updated', handleLocationsUpdate);
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error || !locationData) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Stadt nicht gefunden</h1>
          <p className="text-gray-400 mb-8">Die angeforderte Stadt konnte nicht gefunden werden.</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300">
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const seoData = {
    title: locationData[`title_${currentLang}`] || 
           (currentLang === 'de' 
             ? `Immobilienfotograf in ${locationData.name} | FotografX`
             : `Фотограф недвижимости в ${locationData.name} | FotografX`),
    description: locationData[`description_${currentLang}`] || 
                (currentLang === 'de'
                  ? `Professionelle Immobilienfotografie in ${locationData.name}. Hochwertige Innen- und Außenaufnahmen, 360° virtuelle Rundgänge und Architekturfotografie für Ihre Immobilie.`
                  : `Профессиональная фотография недвижимости в ${locationData.name}. Качественные интерьерные и экстерьерные съемки, виртуальные туры 360° и архитектурная фотография для вашей недвижимости.`),
    keywords: locationData[`meta_keywords_${currentLang}`] || 
             (currentLang === 'de'
               ? `Immobilienfotograf ${locationData.name}, Architekturfotografie ${locationData.name}, Innenaufnahmen ${locationData.name}, 360° virtuelle Rundgänge ${locationData.name}`
               : `фотограф недвижимости ${locationData.name}, архитектурная фотография ${locationData.name}, интерьерные съемки ${locationData.name}, виртуальные туры 360° ${locationData.name}`)
  };

  const localFeatures = [
    {
      icon: Camera,
      title: currentLang === 'de' ? 'Lokale Expertise' : 'Местная экспертиза',
      description: currentLang === 'de' 
        ? `Ich fotografiere regelmäßig Immobilien in ${locationData.name} und kenne die besten Standorte und Lichtverhältnisse der Stadt.`
        : `Я регулярно фотографирую недвижимость в ${locationData.name} и знаю лучшие локации и условия освещения города.`
    },
    {
      icon: Heart,
      title: currentLang === 'de' ? 'Professioneller Service' : 'Профессиональный сервис',
      description: currentLang === 'de' 
        ? `Individuelle Beratung und maßgeschneiderte Immobilienfotografie-Konzepte für Makler und Eigentümer in ${locationData.name}.`
        : `Индивидуальные консультации и персонализированные концепции фотографии недвижимости для риелторов и собственников в ${locationData.name}.`
    },
    {
      icon: Users,
      title: currentLang === 'de' ? 'Zufriedene Kunden' : 'Довольные клиенты',
      description: currentLang === 'de' 
        ? `Viele zufriedene Makler und Immobilieneigentümer aus ${locationData.name} vertrauen auf meine professionelle Immobilienfotografie.`
        : `Многие довольные риелторы и собственники недвижимости из ${locationData.name} доверяют моей профессиональной фотографии недвижимости.`
    }
  ];

  return (
    <div className="bg-black text-white">
      <PageTitle title={seoData.title} description={seoData.description} />
      <SEO {...seoData} type="website" url={`/${slug}`} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0 bg-black opacity-60 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: locationData.hero_image && locationData.hero_image.trim() !== ''
              ? `url('${locationData.hero_image}')`
              : "url('https://images.unsplash.com/photo-1519904981063-b0cf448d479e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxnZXJhJTIwZ2VybWFueXxlbnwwfHx8fDE3MjYzNTE5ODh8MA&ixlib=rb-4.0.3&q=85')"
          }}
        ></div>
        <div className="relative z-20 text-center px-4 sm:px-6 lg:px-8">
          <h1 
            className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' ? (
              <>Immobilienfotograf<br />in {locationData.name}</>
            ) : (
              <>Фотограф недвижимости<br />в {locationData.name}</>
            )}
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto text-gray-200">
            {locationData[`hero_subtitle_${currentLang}`] || (currentLang === 'de' 
              ? `Professionelle Immobilienfotografie - Innenräume, Außenaufnahmen und 360° virtuelle Rundgänge in ${locationData.name} und Umgebung.`
              : `Профессиональная фотография недвижимости - интерьеры, экстерьеры и виртуальные туры 360° в ${locationData.name} и окрестностях.`
            )}
          </p>
          <Link 
            to="/booking"
            className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold text-lg hover:bg-gray-200 transition-all duration-300 transform hover:scale-105"
          >
            {currentLang === 'de' ? 'Jetzt Buchen' : 'Забронировать'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Custom Content Section */}
      {locationData[`content_${currentLang}`] && (
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert prose-lg max-w-none">
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: locationData[`content_${currentLang}`].replace(/\n/g, '<br/>') 
                }} 
              />
            </div>
          </div>
        </section>
      )}

      {/* Local Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {currentLang === 'de' ? `Warum FotografX in ${locationData.name}?` : `Почему FotografX в ${locationData.name}?`}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {currentLang === 'de' 
                ? `Professionelle Fotografie in ${locationData.name} mit persönlicher Note`
                : `Профессиональная фотография в ${locationData.name} с персональным подходом`
              }
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {localFeatures.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-black border border-gray-800">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white text-black rounded-full mb-6">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 
                  className="text-xl font-semibold mb-4"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {currentLang === 'de' ? `Meine Services in ${locationData.name}` : `Мои услуги в ${locationData.name}`}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {currentLang === 'de' 
                ? `Professionelle Fotografie für jeden Anlass in ${locationData.name}`
                : `Профессиональная фотография для любого случая в ${locationData.name}`
              }
            </p>
          </div>
          
          {services.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div key={service.id} className="bg-gray-900 p-6 border border-gray-800 hover:border-gray-600 transition-colors duration-300">
                  <h3 
                    className="text-xl font-semibold mb-4"
                    style={{ fontFamily: '"Times New Roman", serif' }}
                  >
                    {service[`name_${currentLang}`]}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {service[`description_${currentLang}`]}
                  </p>
                  <p className="text-white font-semibold mb-4">
                    {service.price}
                  </p>
                  <Link 
                    to="/booking"
                    className="inline-flex items-center text-white hover:text-gray-300 transition-colors duration-300"
                  >
                    {currentLang === 'de' ? 'Jetzt Buchen' : 'Забронировать'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' ? `Bereit für Ihr Shooting in ${locationData.name}?` : `Готовы к съемке в ${locationData.name}?`}
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            {locationData[`contact_text_${currentLang}`] || (currentLang === 'de' 
              ? `Ich bin in Ronneburg ansässig, aber arbeite regelmäßig in ${locationData.name}. Kontaktieren Sie mich für ein unverbindliches Beratungsgespräch.`
              : `Я базируюсь в Роннебурге, но регулярно работаю в ${locationData.name}. Свяжитесь со мной для бесплатной консультации.`
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/booking"
              className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold text-lg hover:bg-gray-200 transition-all duration-300"
            >
              {currentLang === 'de' ? 'Kostenlose Beratung' : 'Бесплатная консультация'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a 
              href="tel:+4912345678"
              className="inline-flex items-center px-8 py-4 border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
            >
              {currentLang === 'de' ? 'Jetzt Anrufen' : 'Позвонить сейчас'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DynamicCityPage;